import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, imgSrc, uploadImage, uploadTechFile, formatApiError } from "../../lib/api";

const empty = {
  slug: "", title_it: "", title_en: "", description_it: "", description_en: "",
  category: "windows", material_tags: [], location: "", year: "", architect: "",
  materials: [], dimensions: "", specs: {}, featured: false, order: 0,
  cover_image: "", before_image: "", gallery: [], technical_files: [],
};

const specsToText = (specs) => Object.entries(specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
const textToSpecs = (text) => {
  const out = {};
  text.split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  });
  return out;
};

export default function ProjectForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [materialsText, setMaterialsText] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [specsText, setSpecsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/projects/${id}`).then(({ data }) => {
      setForm({ ...empty, ...data });
      setMaterialsText((data.materials || []).join(", "));
      setTagsText((data.material_tags || []).join(", "));
      setSpecsText(specsToText(data.specs));
    }).catch(() => {
      toast.error("Progetto non trovato");
      navigate("/admin");
    });
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImageUpload = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(target);
    try {
      for (const file of files) {
        const { storage_path } = await uploadImage(file);
        if (target === "cover") set("cover_image", storage_path);
        else if (target === "before") set("before_image", storage_path);
        else setForm((f) => ({ ...f, gallery: [...f.gallery, storage_path] }));
      }
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const handleTechUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading("tech");
    try {
      for (const file of files) {
        const meta = await uploadTechFile(file);
        setForm((f) => ({ ...f, technical_files: [...f.technical_files, meta] }));
      }
      toast.success("File tecnico caricato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      materials: materialsText.split(",").map((s) => s.trim()).filter(Boolean),
      material_tags: tagsText.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      specs: textToSpecs(specsText),
    };
    try {
      if (isEdit) await api.put(`/projects/${id}`, payload);
      else await api.post("/projects", payload);
      toast.success(isEdit ? "Progetto aggiornato" : "Progetto creato");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "border-[#262420] bg-[#080808] text-[#F3EFE6] focus-visible:ring-[#C8A86B]";
  const labelCls = "text-xs font-mono uppercase tracking-wider text-[#827D72]";

  return (
    <div data-testid="project-form-page" className="min-h-screen bg-[#080808] pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" data-testid="form-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#827D72] transition-colors hover:text-[#C8A86B]">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <h1 className="mt-4 font-serif text-3xl text-[#F3EFE6]">{isEdit ? "Modifica Progetto" : "Nuovo Progetto"}</h1>

        <form onSubmit={submit} className="mt-10 flex flex-col gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Titolo (IT) *</Label>
              <Input data-testid="field-title-it" required value={form.title_it} onChange={(e) => set("title_it", e.target.value)} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Title (EN) *</Label>
              <Input data-testid="field-title-en" required value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className={labelCls}>Descrizione (IT)</Label>
            <Textarea data-testid="field-description-it" rows={3} value={form.description_it} onChange={(e) => set("description_it", e.target.value)} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className={labelCls}>Description (EN)</Label>
            <Textarea data-testid="field-description-en" rows={3} value={form.description_en} onChange={(e) => set("description_en", e.target.value)} className={inputCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Categoria</Label>
              <select data-testid="field-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9 w-full border border-[#262420] bg-[#080808] px-3 text-sm text-[#F3EFE6] focus:outline-none focus:ring-1 focus:ring-[#C8A86B]">
                <option value="windows">Finestre / Windows</option>
                <option value="doors">Porte / Doors</option>
                <option value="facades">Facciate / Facades</option>
                <option value="outdoor">Vetrate & Pergole / Glass Rooms</option>
                <option value="awnings">Tende da Sole / Awnings</option>
                <option value="railings">Balaustre in Vetro / Glass Balustrades</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Luogo</Label>
              <Input data-testid="field-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Anno</Label>
              <Input data-testid="field-year" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Architetto</Label>
              <Input data-testid="field-architect" value={form.architect} onChange={(e) => set("architect", e.target.value)} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Dimensioni</Label>
              <Input data-testid="field-dimensions" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Materiali (separati da virgola)</Label>
              <Input data-testid="field-materials" value={materialsText} onChange={(e) => setMaterialsText(e.target.value)} className={inputCls} placeholder="Bronzo Brunito, Vetro Triplo" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className={labelCls}>Tag materiale (bronzo, corten, ferro…)</Label>
              <Input data-testid="field-material-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} className={inputCls} placeholder="bronzo, vetro" />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className={labelCls}>Specifiche tecniche (una per riga: "Chiave: Valore")</Label>
            <Textarea data-testid="field-specs" rows={4} value={specsText} onChange={(e) => setSpecsText(e.target.value)} className={inputCls} placeholder={"Trasmittanza termica: Uw = 0.78 W/m²K\nIsolamento acustico: Rw = 44 dB"} />
          </div>

          <label className="flex items-center gap-3 text-sm text-[#A39E93]">
            <input type="checkbox" data-testid="field-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#C8A86B]" />
            In evidenza in homepage
          </label>

          {/* Cover image */}
          <div className="flex flex-col gap-2 border border-[#1C1A17] p-5">
            <Label className={labelCls}>Immagine di copertina (max 15MB — jpg, png, webp)</Label>
            {form.cover_image && (
              <div className="relative w-48">
                <img src={imgSrc(form.cover_image)} alt="" className="h-28 w-48 border border-[#262420] object-cover" />
                <button type="button" data-testid="remove-cover-image" onClick={() => set("cover_image", "")} className="absolute -right-2 -top-2 border border-[#262420] bg-[#080808] p-1 text-[#A39E93] hover:text-[#C25D53]"><X size={12} /></button>
              </div>
            )}
            <label data-testid="upload-cover-button" className="inline-flex w-fit cursor-pointer items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#C8A86B]">
              <Upload size={13} /> {uploading === "cover" ? "Caricamento…" : "Carica immagine"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "cover")} />
            </label>
          </div>

          {/* Before image */}
          <div className="flex flex-col gap-2 border border-[#1C1A17] p-5">
            <Label className={labelCls}>Foto "Prima" (opzionale — attiva lo slider Prima/Dopo con la copertina)</Label>
            {form.before_image && (
              <div className="relative w-48">
                <img src={imgSrc(form.before_image)} alt="" className="h-28 w-48 border border-[#262420] object-cover" />
                <button type="button" data-testid="remove-before-image" onClick={() => set("before_image", "")} className="absolute -right-2 -top-2 border border-[#262420] bg-[#080808] p-1 text-[#A39E93] hover:text-[#C25D53]"><X size={12} /></button>
              </div>
            )}
            <label data-testid="upload-before-button" className="inline-flex w-fit cursor-pointer items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#C8A86B]">
              <Upload size={13} /> {uploading === "before" ? "Caricamento…" : "Carica foto prima"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, "before")} />
            </label>
          </div>

          {/* Gallery */}
          <div className="flex flex-col gap-2 border border-[#1C1A17] p-5">
            <Label className={labelCls}>Galleria immagini</Label>
            <div className="flex flex-wrap gap-3">
              {form.gallery.map((g, i) => (
                <div key={g} className="relative">
                  <img src={imgSrc(g)} alt="" className="h-20 w-32 border border-[#262420] object-cover" />
                  <button type="button" data-testid={`remove-gallery-image-${i}`} onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }))} className="absolute -right-2 -top-2 border border-[#262420] bg-[#080808] p-1 text-[#A39E93] hover:text-[#C25D53]"><X size={12} /></button>
                </div>
              ))}
            </div>
            <label data-testid="upload-gallery-button" className="inline-flex w-fit cursor-pointer items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#C8A86B]">
              <Upload size={13} /> {uploading === "gallery" ? "Caricamento…" : "Aggiungi immagini"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(e, "gallery")} />
            </label>
          </div>

          {/* Technical files */}
          <div className="flex flex-col gap-2 border border-[#1C1A17] p-5">
            <Label className={labelCls}>File tecnici (PDF, DWG, DXF, ZIP — max 60MB)</Label>
            <div className="flex flex-col gap-2">
              {form.technical_files.map((f, i) => (
                <div key={f.storage_path || f.name} className="flex items-center justify-between gap-3 border border-[#262420] bg-[#080808] px-3 py-2">
                  <p className="truncate text-xs font-mono text-[#A39E93]">{f.name} · {f.size}</p>
                  <button type="button" data-testid={`remove-tech-file-${i}`} onClick={() => setForm((prev) => ({ ...prev, technical_files: prev.technical_files.filter((_, x) => x !== i) }))} className="text-[#A39E93] hover:text-[#C25D53]"><X size={14} /></button>
                </div>
              ))}
            </div>
            <label data-testid="upload-tech-button" className="inline-flex w-fit cursor-pointer items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#C8A86B]">
              <Upload size={13} /> {uploading === "tech" ? "Caricamento…" : "Carica file tecnico"}
              <input type="file" accept=".pdf,.dwg,.dxf,.zip" multiple className="hidden" onChange={handleTechUpload} />
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" data-testid="save-project-btn" disabled={saving} className="bg-[#C8A86B] px-8 py-3 text-sm uppercase tracking-[0.15em] text-[#080808] transition-colors hover:bg-[#D8BD85] disabled:opacity-50">
              {saving ? "Salvataggio…" : "Salva Progetto"}
            </button>
            <Link to="/admin" data-testid="cancel-project-btn" className="border border-[#262420] px-8 py-3 text-sm uppercase tracking-[0.15em] text-[#A39E93] transition-colors hover:text-[#F3EFE6]">
              Annulla
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
